/**
 * Max OS Cloud — Email Sequence Templates
 *
 * 3 sequences: Welcome (3 emails), Activation (3 emails), Retention (2 emails)
 * Total: 8 emails
 *
 * Each template includes:
 *   - id: unique identifier
 *   - sequence: "welcome" | "activation" | "retention"
 *   - step: position within sequence (1-indexed)
 *   - subject: email subject line
 *   - preview: preview/preheader text
 *   - delayDays: days after signup (0 = immediate)
 *   - bodyHtml: inline-CSS HTML template (email client compatible)
 */

const BRAND = {
  name: "Max OS Cloud",
  baseUrl: "https://maxemployee.com",
  supportUrl: "https://maxemployee.com/support",
  dashboardUrl: "https://maxemployee.com/dashboard",
  logoUrl: "https://maxemployee.com/logo.png",
  unsubscribeUrl: "https://maxemployee.com/unsubscribe",
};

/**
 * Shared email wrapper — dark theme, inline CSS for email client compatibility.
 * All typography uses system font stacks (no web fonts in email).
 */
function emailWrapper({ preheader, content, footerNote }) {
  return `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="color-scheme" content="dark">
  <meta name="supported-color-schemes" content="dark">
  <span style="display:none;font-size:1px;color:#09090B;max-height:0;max-width:0;opacity:0;overflow:hidden;">${preheader}</span>
  <title>${BRAND.name}</title>
  <!--[if mso]>
  <style type="text/css">body, table, td { font-family: Arial, Helvetica, sans-serif !important; }</style>
  <![endif]-->
</head>
<body style="margin:0;padding:0;background-color:#09090B;color:#FAFAFA;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;-webkit-font-smoothing:antialiased;width:100%;-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color:#09090B;">
    <tr>
      <td align="center" style="padding:24px 16px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="600" style="max-width:600px;width:100%;background-color:#18181B;border:1px solid #27272A;">
          <!-- Header -->
          <tr>
            <td style="padding:24px 32px;border-bottom:1px solid #27272A;text-align:center;">
              <span style="font-family:'Courier New',Courier,monospace;font-size:14px;font-weight:700;text-transform:uppercase;letter-spacing:4px;color:#F59E0B;">${BRAND.name.toUpperCase()}</span>
            </td>
          </tr>
          <!-- Body -->
          <tr>
            <td style="padding:32px;">
              ${content}
            </td>
          </tr>
          <!-- CTA Button (optional) -->
          ${footerNote ? `<tr><td style="padding:0 32px 24px;text-align:center;"><p style="font-family:'Courier New',Courier,monospace;font-size:11px;color:#52525B;text-transform:uppercase;letter-spacing:1px;">${footerNote}</p></td></tr>` : ""}
          <!-- Footer -->
          <tr>
            <td style="padding:24px 32px;border-top:1px solid #27272A;text-align:center;">
              <p style="font-size:12px;color:#52525B;margin:0 0 8px;line-height:1.6;">
                &copy; ${new Date().getFullYear()} ${BRAND.name}. All rights reserved.
              </p>
              <p style="font-size:11px;color:#52525B;margin:0;line-height:1.6;">
                <a href="${BRAND.dashboardUrl}" style="color:#A1A1AA;text-decoration:none;margin:0 8px;">Dashboard</a>
                <span style="color:#27272A;">|</span>
                <a href="${BRAND.supportUrl}" style="color:#A1A1AA;text-decoration:none;margin:0 8px;">{t('api_docs.automations.builder.categories.support')}</a>
                <span style="color:#27272A;">|</span>
                <a href="${BRAND.unsubscribeUrl}" style="color:#52525B;text-decoration:underline;margin:0 8px;">Unsubscribe</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/**
 * Reusable CTA button for email templates
 */
function ctaButton({ text, url }) {
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:32px auto;">
  <tr>
    <td style="background-color:#F59E0B;border-radius:0;">
      <a href="${url}" target="_blank" style="display:inline-block;padding:16px 40px;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:#09090B;text-decoration:none;font-family:'Courier New',Courier,monospace;">${text}</a>
    </td>
  </tr>
</table>`;
}

/**
 * Reusable section divider
 */
function divider() {
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="padding:16px 0;"><table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="border-top:1px solid #27272A;font-size:1px;line-height:1px;">&nbsp;</td></tr></table></td></tr></table>`;
}

// ─── WELCOME SEQUENCE ────────────────────────────────────────────────

const WELCOME_EMAIL_1 = {
  id: "welcome-01",
  sequence: "welcome",
  step: 1,
  subject: "Welcome to Max OS Cloud — Your AI worker is ready",
  preview: "Your autonomous AI agent is live. Here's how to access it in the next 60 seconds.",
  delayDays: 0,
  bodyHtml: emailWrapper({
    preheader: "Your autonomous AI agent is live. Here's how to access it.",
    content: `
      <h1 style="font-size:24px;font-weight:900;text-transform:uppercase;letter-spacing:-1px;color:#FAFAFA;margin:0 0 8px;line-height:1.2;">Welcome to Max OS Cloud</h1>
      <p style="font-size:11px;font-family:'Courier New',Courier,monospace;text-transform:uppercase;letter-spacing:3px;color:#F59E0B;margin:0 0 24px;">Your AI worker is live</p>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">You just deployed an autonomous AI agent that works 24/7 — no code, no setup, no waiting.</p>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">Here's how to access your agent right now:</p>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;background-color:#09090B;border:1px solid #27272A;margin:16px 0;">
        <tr>
          <td style="padding:20px 24px;">
            <p style="font-size:13px;color:#FAFAFA;margin:0 0 8px;"><strong style="color:#F59E0B;">Step 1:</strong> Open your dashboard</p>
            <p style="font-size:13px;color:#FAFAFA;margin:0 0 8px;"><strong style="color:#F59E0B;">Step 2:</strong> Click "Start Tour" for a guided walkthrough</p>
            <p style="font-size:13px;color:#FAFAFA;margin:0;"><strong style="color:#F59E0B;">Step 3:</strong> Say hello to your AI agent</p>
          </td>
        </tr>
      </table>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">That's it. Your agent is already running in the cloud.</p>
      ${ctaButton({ text: "Open Dashboard", url: BRAND.dashboardUrl })}
      <p style="font-size:13px;color:#52525B;line-height:1.6;text-align:center;">Need help? Reply to this email or visit <a href="${BRAND.supportUrl}" style="color:#A1A1AA;">our support center</a>.</p>
    `,
    footerNote: null,
  }),
};

const WELCOME_EMAIL_2 = {
  id: "welcome-02",
  sequence: "welcome",
  step: 2,
  subject: "Your first agent in 5 minutes",
  preview: "A quick-start guide to get your first automation running today.",
  delayDays: 2,
  bodyHtml: emailWrapper({
    preheader: "A quick-start guide to get your first automation running today.",
    content: `
      <h1 style="font-size:24px;font-weight:900;text-transform:uppercase;letter-spacing:-1px;color:#FAFAFA;margin:0 0 8px;line-height:1.2;">Your First Agent</h1>
      <p style="font-size:11px;font-family:'Courier New',Courier,monospace;text-transform:uppercase;letter-spacing:3px;color:#F59E0B;margin:0 0 24px;">5-minute quick start</p>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">Two days in — have you said hello to your agent yet?</p>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">Here's the fastest way to see results:</p>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;background-color:#09090B;border:1px solid #27272A;margin:16px 0;">
        <tr>
          <td style="padding:20px 24px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
              <tr>
                <td style="padding:8px 0;border-bottom:1px solid #18181B;">
                  <p style="font-size:11px;font-family:'Courier New',Courier,monospace;color:#F59E0B;margin:0 0 4px;text-transform:uppercase;letter-spacing:2px;">01 / Open the chat</p>
                  <p style="font-size:14px;color:#A1A1AA;margin:0;line-height:1.6;">Go to your dashboard and click the agent chat panel.</p>
                </td>
              </tr>
              <tr>
                <td style="padding:8px 0;border-bottom:1px solid #18181B;">
                  <p style="font-size:11px;font-family:'Courier New',Courier,monospace;color:#F59E0B;margin:0 0 4px;text-transform:uppercase;letter-spacing:2px;">02 / Ask anything</p>
                  <p style="font-size:14px;color:#A1A1AA;margin:0;line-height:1.6;">Try: "Summarize my inbox" or "Draft a reply to my last email."</p>
                </td>
              </tr>
              <tr>
                <td style="padding:8px 0;">
                  <p style="font-size:11px;font-family:'Courier New',Courier,monospace;color:#F59E0B;margin:0 0 4px;text-transform:uppercase;letter-spacing:2px;">03 / Watch it work</p>
                  <p style="font-size:14px;color:#A1A1AA;margin:0;line-height:1.6;">Your agent executes tasks autonomously — no manual steps.</p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">Most users save their first hour of work within the first session.</p>
      ${ctaButton({ text: "Launch Your Agent", url: BRAND.dashboardUrl })}
    `,
    footerNote: null,
  }),
};

const WELCOME_EMAIL_3 = {
  id: "welcome-03",
  sequence: "welcome",
  step: 3,
  subject: "Meet your AI agent — What it can do",
  preview: "From customer support to data entry: see what Max OS Cloud automates for you.",
  delayDays: 4,
  bodyHtml: emailWrapper({
    preheader: "From customer support to data entry: see what Max OS Cloud automates.",
    content: `
      <h1 style="font-size:24px;font-weight:900;text-transform:uppercase;letter-spacing:-1px;color:#FAFAFA;margin:0 0 8px;line-height:1.2;">Meet Your AI Agent</h1>
      <p style="font-size:11px;font-family:'Courier New',Courier,monospace;text-transform:uppercase;letter-spacing:3px;color:#F59E0B;margin:0 0 24px;">What it can do for you</p>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 24px;">Your Max OS Cloud agent isn't just a chatbot. It's an autonomous worker that handles real business tasks.</p>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;background-color:#09090B;border:1px solid #27272A;margin:0 0 24px;">
        <tr>
          <td style="padding:20px 24px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
              <tr>
                <td style="padding:12px 0;border-bottom:1px solid #18181B;">
                  <p style="font-size:13px;font-weight:700;color:#FAFAFA;margin:0 0 4px;text-transform:uppercase;">Customer Support</p>
                  <p style="font-size:13px;color:#A1A1AA;margin:0;line-height:1.6;">Auto-respond to FAQs, triage tickets, escalate with full context.</p>
                </td>
              </tr>
              <tr>
                <td style="padding:12px 0;border-bottom:1px solid #18181B;">
                  <p style="font-size:13px;font-weight:700;color:#FAFAFA;margin:0 0 4px;text-transform:uppercase;">Data Entry &amp; Processing</p>
                  <p style="font-size:13px;color:#A1A1AA;margin:0;line-height:1.6;">Extract, transform, and sync data across your tools — zero manual work.</p>
                </td>
              </tr>
              <tr>
                <td style="padding:12px 0;border-bottom:1px solid #18181B;">
                  <p style="font-size:13px;font-weight:700;color:#FAFAFA;margin:0 0 4px;text-transform:uppercase;">Email &amp; Calendar</p>
                  <p style="font-size:13px;color:#A1A1AA;margin:0;line-height:1.6;">Draft replies, schedule meetings, follow up automatically.</p>
                </td>
              </tr>
              <tr>
                <td style="padding:12px 0;">
                  <p style="font-size:13px;font-weight:700;color:#FAFAFA;margin:0 0 4px;text-transform:uppercase;">Proactive Monitoring</p>
                  <p style="font-size:13px;color:#A1A1AA;margin:0;line-height:1.6;">Detect anomalies, flag issues, and alert your team before problems escalate.</p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">The more you use it, the smarter it gets. Your agent learns your workflows over time.</p>
      ${ctaButton({ text: "Explore Use Cases", url: `${BRAND.baseUrl}/marketplace` })}
    `,
    footerNote: null,
  }),
};

// ─── ACTIVATION SEQUENCE ──────────────────────────────────────────────

const ACTIVATION_EMAIL_4 = {
  id: "activation-01",
  sequence: "activation",
  step: 1,
  subject: "Have you connected your first channel?",
  preview: "Link WhatsApp or Telegram to your agent in under 3 minutes.",
  delayDays: 7,
  bodyHtml: emailWrapper({
    preheader: "Link WhatsApp or Telegram to your agent in under 3 minutes.",
    content: `
      <h1 style="font-size:24px;font-weight:900;text-transform:uppercase;letter-spacing:-1px;color:#FAFAFA;margin:0 0 8px;line-height:1.2;">Connect a Channel</h1>
      <p style="font-size:11px;font-family:'Courier New',Courier,monospace;text-transform:uppercase;letter-spacing:3px;color:#F59E0B;margin:0 0 24px;">Your agent is ready to talk</p>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">One week in — your agent is running, but it's still working in isolation.</p>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">The real power unlocks when you connect it to the channels your customers already use.</p>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;background-color:#09090B;border:1px solid #27272A;margin:16px 0;">
        <tr>
          <td style="padding:20px 24px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
              <tr>
                <td style="padding:8px 0;border-bottom:1px solid #18181B;">
                  <p style="font-size:13px;font-weight:700;color:#FAFAFA;margin:0 0 4px;">{t('referral.whatsapp')}</p>
                  <p style="font-size:13px;color:#A1A1AA;margin:0;line-height:1.6;">Connect your business number. Your agent handles messages 24/7.</p>
                </td>
              </tr>
              <tr>
                <td style="padding:8px 0;border-bottom:1px solid #18181B;">
                  <p style="font-size:13px;font-weight:700;color:#FAFAFA;margin:0 0 4px;">{t('referral.telegram')}</p>
                  <p style="font-size:13px;color:#A1A1AA;margin:0;line-height:1.6;">Set up a bot in minutes. Perfect for teams and communities.</p>
                </td>
              </tr>
              <tr>
                <td style="padding:8px 0;">
                  <p style="font-size:13px;font-weight:700;color:#FAFAFA;margin:0 0 4px;">Web Chat</p>
                  <p style="font-size:13px;color:#A1A1AA;margin:0;line-height:1.6;">Embed a chat widget on your website. Zero code required.</p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">Setup takes under 3 minutes per channel.</p>
      ${ctaButton({ text: "Connect a Channel", url: `${BRAND.dashboardUrl}/channels` })}
    `,
    footerNote: null,
  }),
};

const ACTIVATION_EMAIL_5 = {
  id: "activation-02",
  sequence: "activation",
  step: 2,
  subject: "Upload your knowledge base — Make your agent smarter",
  preview: "Train your agent with your docs, FAQs, and processes. Here's how.",
  delayDays: 10,
  bodyHtml: emailWrapper({
    preheader: "Train your agent with your docs, FAQs, and processes.",
    content: `
      <h1 style="font-size:24px;font-weight:900;text-transform:uppercase;letter-spacing:-1px;color:#FAFAFA;margin:0 0 8px;line-height:1.2;">Make Your Agent Smarter</h1>
      <p style="font-size:11px;font-family:'Courier New',Courier,monospace;text-transform:uppercase;letter-spacing:3px;color:#F59E0B;margin:0 0 24px;">Upload your knowledge base</p>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">Right now, your agent knows the basics. But it doesn't know <em>your</em> business yet.</p>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">Upload your knowledge base and your agent becomes a domain expert — in minutes.</p>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;background-color:#09090B;border:1px solid #27272A;margin:16px 0;">
        <tr>
          <td style="padding:20px 24px;">
            <p style="font-size:13px;color:#FAFAFA;margin:0 0 8px;"><strong style="color:#F59E0B;">Supported formats:</strong> PDF, DOCX, TXT, Markdown, CSV</p>
            <p style="font-size:13px;color:#FAFAFA;margin:0 0 8px;"><strong style="color:#F59E0B;">What to upload:</strong> FAQs, product docs, SOPs, policies, past tickets</p>
            <p style="font-size:13px;color:#FAFAFA;margin:0;"><strong style="color:#F59E0B;">Result:</strong> Your agent answers with your data, your tone, your accuracy.</p>
          </td>
        </tr>
      </table>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">Businesses that upload a knowledge base see 3x faster resolution times.</p>
      ${ctaButton({ text: "Upload Knowledge Base", url: `${BRAND.dashboardUrl}/knowledge` })}
    `,
    footerNote: null,
  }),
};

const ACTIVATION_EMAIL_6 = {
  id: "activation-03",
  sequence: "activation",
  step: 3,
  subject: "Your agent's first week — What to expect",
  preview: "Expected metrics, benchmarks, and tips for your first 7 days.",
  delayDays: 14,
  bodyHtml: emailWrapper({
    preheader: "Expected metrics, benchmarks, and tips for your first 7 days.",
    content: `
      <h1 style="font-size:24px;font-weight:900;text-transform:uppercase;letter-spacing:-1px;color:#FAFAFA;margin:0 0 8px;line-height:1.2;">Your Agent's First Week</h1>
      <p style="font-size:11px;font-family:'Courier New',Courier,monospace;text-transform:uppercase;letter-spacing:3px;color:#F59E0B;margin:0 0 24px;">What to expect</p>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">Two weeks in — here's what a healthy first week looks like for most Max OS Cloud users:</p>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;background-color:#09090B;border:1px solid #27272A;margin:16px 0;">
        <tr>
          <td style="padding:20px 24px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
              <tr>
                <td style="padding:8px 0;border-bottom:1px solid #18181B;">
                  <p style="font-size:11px;font-family:'Courier New',Courier,monospace;color:#F59E0B;margin:0 0 4px;text-transform:uppercase;letter-spacing:2px;">Metric 1</p>
                  <p style="font-size:14px;color:#FAFAFA;margin:0;line-height:1.6;"><strong>50+ tasks</strong> — Average tasks completed in week one</p>
                </td>
              </tr>
              <tr>
                <td style="padding:8px 0;border-bottom:1px solid #18181B;">
                  <p style="font-size:11px;font-family:'Courier New',Courier,monospace;color:#F59E0B;margin:0 0 4px;text-transform:uppercase;letter-spacing:2px;">Metric 2</p>
                  <p style="font-size:14px;color:#FAFAFA;margin:0;line-height:1.6;"><strong>60% faster</strong> — Average response time reduction</p>
                </td>
              </tr>
              <tr>
                <td style="padding:8px 0;">
                  <p style="font-size:11px;font-family:'Courier New',Courier,monospace;color:#F59E0B;margin:0 0 4px;text-transform:uppercase;letter-spacing:2px;">Metric 3</p>
                  <p style="font-size:14px;color:#FAFAFA;margin:0;line-height:1.6;"><strong>15 hrs/week</strong> — Average time saved per team</p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;"><strong style="color:#FAFAFA;">Pro tip:</strong> Connect at least one channel (WhatsApp or Telegram) and upload a knowledge base. Users who do both see 2x the impact.</p>
      ${ctaButton({ text: "View Your Metrics", url: `${BRAND.dashboardUrl}/analytics` })}
    `,
    footerNote: null,
  }),
};

// ─── RETENTION SEQUENCE ───────────────────────────────────────────────

const RETENTION_EMAIL_7 = {
  id: "retention-01",
  sequence: "retention",
  step: 1,
  subject: "Level up — Advanced features you're missing",
  preview: "Skills, automations, webhooks: unlock the full power of Max OS Cloud.",
  delayDays: 21,
  bodyHtml: emailWrapper({
    preheader: "Skills, automations, webhooks: unlock the full power of Max OS Cloud.",
    content: `
      <h1 style="font-size:24px;font-weight:900;text-transform:uppercase;letter-spacing:-1px;color:#FAFAFA;margin:0 0 8px;line-height:1.2;">Level Up</h1>
      <p style="font-size:11px;font-family:'Courier New',Courier,monospace;text-transform:uppercase;letter-spacing:3px;color:#F59E0B;margin:0 0 24px;">Advanced features you're missing</p>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">Three weeks in — you've got the basics down. Now it's time to unlock the features that separate power users from everyone else.</p>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;background-color:#09090B;border:1px solid #27272A;margin:16px 0;">
        <tr>
          <td style="padding:20px 24px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
              <tr>
                <td style="padding:12px 0;border-bottom:1px solid #18181B;">
                  <p style="font-size:13px;font-weight:700;color:#FAFAFA;margin:0 0 4px;text-transform:uppercase;">{t('common.skills')}</p>
                  <p style="font-size:13px;color:#A1A1AA;margin:0;line-height:1.6;">Pre-built capability packs — CRM, email, scheduling, reporting. Install in one click.</p>
                </td>
              </tr>
              <tr>
                <td style="padding:12px 0;border-bottom:1px solid #18181B;">
                  <p style="font-size:13px;font-weight:700;color:#FAFAFA;margin:0 0 4px;text-transform:uppercase;">Automations</p>
                  <p style="font-size:13px;color:#A1A1AA;margin:0;line-height:1.6;">Chain multiple actions into workflows. Trigger → Process → Notify → Log.</p>
                </td>
              </tr>
              <tr>
                <td style="padding:12px 0;">
                  <p style="font-size:13px;font-weight:700;color:#FAFAFA;margin:0 0 4px;text-transform:uppercase;">Webhooks</p>
                  <p style="font-size:13px;color:#A1A1AA;margin:0;line-height:1.6;">Connect Max OS Cloud to any external system. Real-time event-driven automation.</p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">These features are included in your plan — no upgrade needed.</p>
      ${ctaButton({ text: "Explore Advanced Features", url: `${BRAND.baseUrl}/marketplace` })}
    `,
    footerNote: null,
  }),
};

const RETENTION_EMAIL_8 = {
  id: "retention-02",
  sequence: "retention",
  step: 2,
  subject: "Your first month — Here's what you accomplished",
  preview: "A summary of your agent's impact and what's next for your business.",
  delayDays: 30,
  bodyHtml: emailWrapper({
    preheader: "A summary of your agent's impact and what's next for your business.",
    content: `
      <h1 style="font-size:24px;font-weight:900;text-transform:uppercase;letter-spacing:-1px;color:#FAFAFA;margin:0 0 8px;line-height:1.2;">Your First Month</h1>
      <p style="font-size:11px;font-family:'Courier New',Courier,monospace;text-transform:uppercase;letter-spacing:3px;color:#F59E0B;margin:0 0 24px;">Here's what you accomplished</p>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">Thirty days ago, you deployed an autonomous AI agent. Here's what that decision has meant for your business:</p>
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;background-color:#09090B;border:1px solid #27272A;margin:16px 0;">
        <tr>
          <td style="padding:20px 24px;">
            <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">
              <tr>
                <td style="padding:8px 0;border-bottom:1px solid #18181B;text-align:center;">
                  <p style="font-size:32px;font-weight:900;color:#F59E0B;margin:0;font-family:'Courier New',Courier,monospace;">15+</p>
                  <p style="font-size:11px;color:#A1A1AA;margin:4px 0 0;text-transform:uppercase;letter-spacing:1px;">Hours saved per week</p>
                </td>
              </tr>
              <tr>
                <td style="padding:8px 0;border-bottom:1px solid #18181B;text-align:center;">
                  <p style="font-size:32px;font-weight:900;color:#F59E0B;margin:0;font-family:'Courier New',Courier,monospace;">60%</p>
                  <p style="font-size:11px;color:#A1A1AA;margin:4px 0 0;text-transform:uppercase;letter-spacing:1px;">Faster response times</p>
                </td>
              </tr>
              <tr>
                <td style="padding:8px 0;text-align:center;">
                  <p style="font-size:32px;font-weight:900;color:#F59E0B;margin:0;font-family:'Courier New',Courier,monospace;">24/7</p>
                  <p style="font-size:11px;color:#A1A1AA;margin:4px 0 0;text-transform:uppercase;letter-spacing:1px;">Agent availability</p>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
      <p style="font-size:15px;color:#A1A1AA;line-height:1.7;margin:0 0 16px;">Ready to scale? Upgrading unlocks multi-agent systems, custom integrations, and a dedicated account manager.</p>
      ${ctaButton({ text: "View Upgrade Options", url: `${BRAND.baseUrl}/pricing` })}
      <p style="font-size:13px;color:#52525B;line-height:1.6;text-align:center;">Thank you for trusting Max OS Cloud. We're just getting started.</p>
    `,
    footerNote: null,
  }),
};

// ─── EXPORTS ──────────────────────────────────────────────────────────

export const EMAIL_SEQUENCES = [
  // Welcome Sequence
  WELCOME_EMAIL_1,
  WELCOME_EMAIL_2,
  WELCOME_EMAIL_3,
  // Activation Sequence
  ACTIVATION_EMAIL_4,
  ACTIVATION_EMAIL_5,
  ACTIVATION_EMAIL_6,
  // Retention Sequence
  RETENTION_EMAIL_7,
  RETENTION_EMAIL_8,
];

export const EMAIL_SEQUENCE_MAP = {
  welcome: [WELCOME_EMAIL_1, WELCOME_EMAIL_2, WELCOME_EMAIL_3],
  activation: [ACTIVATION_EMAIL_4, ACTIVATION_EMAIL_5, ACTIVATION_EMAIL_6],
  retention: [RETENTION_EMAIL_7, RETENTION_EMAIL_8],
};

export const SEQUENCE_META = {
  welcome: {
    label: "Welcome",
    description: "Onboarding sequence — first access, quick start, use cases",
    emailCount: 3,
    totalDays: 4,
  },
  activation: {
    label: "Activation",
    description: "Feature adoption — channel connection, knowledge base, metrics",
    emailCount: 3,
    totalDays: 14,
  },
  retention: {
    label: "Retention",
    description: "Expansion & loyalty — advanced features, monthly summary, upgrade",
    emailCount: 2,
    totalDays: 30,
  },
};

export default EMAIL_SEQUENCES;
